import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router';
import { Tabs } from '@/components/application/tabs/tabs';
import { PdfViewer } from '@/components/application/pdf-viewer/pdf-viewer';
import { Badge } from '@/components/base/badges/badges';
import { ArrowLeft, AlertCircle, CheckCircle, XCircle, MagicWand01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';
import { ProgressBar } from '@/components/base/progress-indicators/progress-indicators';
import { containerService } from '@/data/mock-containers';
import { setContainerResolved, isContainerResolved } from '@/data/mock-triage-state';
import { useTranslation } from 'react-i18next';

// Import URL of test PDF bundled by Vite
// PDF di test ora servito dalla cartella public
const testPdfUrl = "/document-review-test.pdf";

type TriageDocument = {
    id: string;
    name: string;
    fileUrl: string;
    attrs: Record<string, string | number>;
};

const COMMON_ATTRIBUTES = [
    'invoiceNumber',
    'amount',
    'currency',
    'issueDate',
    'dueDate',
    'shipper',
    'consignee',
    'containerNumber',
    'blNumber',
];

const buildMockDocs = (): TriageDocument[] => {
    const base: Array<Partial<TriageDocument>> = [
        { id: 'd1', name: 'Invoice.pdf' },
        { id: 'd2', name: 'Packing List.pdf' },
        { id: 'd3', name: 'Bill of Lading.pdf' },
        { id: 'd4', name: 'Customs.pdf' },
    ];
    return base.map((b, i) => {
        const attrs: Record<string, string | number> = {};
        COMMON_ATTRIBUTES.forEach((k) => {
            if (k === 'amount') attrs[k] = 1000 + i * 25;
            else if (k === 'currency') attrs[k] = 'EUR';
            else if (k.endsWith('Date')) attrs[k] = `2025-0${(i % 9) + 1}-15`;
            else if (k === 'containerNumber') attrs[k] = `CST-${100 + i}`;
            else if (k === 'blNumber') attrs[k] = `BL-${200 + i}`;
            else attrs[k] = `${k}-${i + 1}`;
        });
        return {
            id: b.id as string,
            name: b.name as string,
            fileUrl: testPdfUrl as string,
            attrs,
        };
    });
};

export const ShipperContainerTriagePage = () => {
    const { t } = useTranslation(['pages:containers', 'common']);
    const { id } = useParams<{ id: string }>();
    const documents = useMemo(() => buildMockDocs(), []);
    const [selectedId, setSelectedId] = useState<string>(documents[0].id);
    const selectedDoc = documents.find((d) => d.id === selectedId)!;

    // Matrix state (mutabile per poterla marcare come risolta)
    const [matrix, setMatrix] = useState(() =>
        COMMON_ATTRIBUTES.map((attr) => documents.map((doc) => ({ ok: Math.random() > 0.5, docId: doc.id, attr })))
    );

    // Discrepanze per doc (mutabile)
    const [discrepancies, setDiscrepancies] = useState<Record<string, Set<string>>>(() => {
        const map: Record<string, Set<string>> = {};
        documents.forEach((d) => {
            const s = new Set<string>();
            COMMON_ATTRIBUTES.forEach((a) => { if (Math.random() > 0.65) s.add(a); });
            map[d.id] = s;
        });
        return map;
    });

    // Container number alfanumerico
    const containerNumber = useMemo(() => {
        if (!id) return id;
        const c = containerService.getById(id);
        return c?.containerNumber || id;
    }, [id]);

    // Reset mock data se container non è risolto
    useEffect(() => {
        if (id && !isContainerResolved(id)) {
            // Ripristina matrice con valori random
            setMatrix(() =>
                COMMON_ATTRIBUTES.map((attr) => documents.map((doc) => ({ ok: Math.random() > 0.5, docId: doc.id, attr })))
            );
            // Ripristina discrepanze random
            const map: Record<string, Set<string>> = {};
            documents.forEach((d) => {
                const s = new Set<string>();
                COMMON_ATTRIBUTES.forEach((a) => { if (Math.random() > 0.65) s.add(a); });
                map[d.id] = s;
            });
            setDiscrepancies(map);
        }
    }, [id, documents]);

    // Modal & progress per risoluzione AI
    const [openAI, setOpenAI] = useState(false);
    const [progress, setProgress] = useState(0);

    const startAIRun = () => {
        setOpenAI(true);
        setProgress(0);
        const step = 12;
        const timer = setInterval(() => {
            setProgress((p) => {
                const next = Math.min(100, p + step);
                if (next === 100) clearInterval(timer);
                return next;
            });
        }, 250);
    };

    const onCloseModal = (open: boolean) => {
        setOpenAI(open);
        if (!open && progress === 100) {
            // Tutti OK su matrice
            setMatrix((prev) => prev.map((row) => row.map((cell) => ({ ...cell, ok: true }))));
            // Nessuna discrepanza
            const cleared: Record<string, Set<string>> = {};
            documents.forEach((d) => { cleared[d.id] = new Set<string>(); });
            setDiscrepancies(cleared);
            // Salva stato risoluzione
            if (id) {
                setContainerResolved(id, true);
            }
        }
    };

    const [formValues, setFormValues] = useState<Record<string, string | number>>(selectedDoc.attrs);

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const nextId = e.target.value;
        setSelectedId(nextId);
        const nd = documents.find((d) => d.id === nextId)!;
        setFormValues(nd.attrs);
    };

    const handleFormChange = (k: string, v: string) => {
        setFormValues((prev) => ({ ...prev, [k]: v }));
    };

    const tabItems = [
        { id: 'matrix', label: t('triage.tabs.matrix') },
        { id: 'visual', label: t('triage.tabs.visual') },
        { id: 'raw', label: t('triage.tabs.raw') },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link to={`/shipper/containers/${id}?tab=documents`} className="text-tertiary hover:text-primary">
                        <ArrowLeft className="size-5" />
                    </Link>
                    <div>
                        <h1 className="text-2xl font-semibold text-primary">{t('triage.title')}</h1>
                        <p className="text-tertiary mt-1">{t('triage.subtitle')} {containerNumber}</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Button size="md" color="primary" iconLeading={MagicWand01} onClick={startAIRun}>
                        {t('triage.resolveWithAI')}
                    </Button>
                </div>
            </div>

            <Tabs defaultSelectedKey={tabItems[0].id}>
                <Tabs.List items={tabItems}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* Tab: Matrice */}
                <Tabs.Panel id="matrix" className="space-y-4">
                    <div className="overflow-auto rounded-xl bg-primary ring-1 ring-secondary">
                        <table className="min-w-full divide-y divide-secondary">
                            <thead className="bg-primary sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-tertiary">{t('triage.matrix.attribute')}</th>
                                    {documents.map((d) => (
                                        <th key={d.id} className="px-4 py-3 text-left text-xs font-semibold text-tertiary">
                                            {d.name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {COMMON_ATTRIBUTES.map((attr, rIdx) => (
                                    <tr key={attr}>
                                        <td className="px-4 py-3 text-sm text-primary font-medium whitespace-nowrap">{attr}</td>
                                        {documents.map((d, cIdx) => {
                                            const cell = matrix[rIdx][cIdx];
                                            const ok = cell.ok;
                                            const Icon = ok ? CheckCircle : XCircle;
                                            const color = ok ? 'text-success-600' : 'text-error-600';
                                            return (
                                                <td key={d.id} className="px-4 py-3 whitespace-nowrap">
                                                    <div className={`inline-flex items-center gap-1 ${color}`}>
                                                        <Icon className="size-4" />
                                                        <span className="text-xs">{ok ? t('triage.matrix.ok') : t('triage.matrix.ko')}</span>
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Tabs.Panel>

                {/* Tab: Visual */}
                <Tabs.Panel id="visual" className="space-y-4">
                    <div className="grid grid-cols-12 gap-4">
                        {/* Col 1 - 25% */}
                        <div className="col-span-12 md:col-span-3 rounded-xl bg-primary p-3 ring-1 ring-secondary">
                            <div className="space-y-2">
                                {documents.map((d) => {
                                    const hasWarn = discrepancies[d.id]?.size && discrepancies[d.id]!.size > 0;
                                    return (
                                        <button
                                            key={d.id}
                                            onClick={() => setSelectedId(d.id)}
                                            className={`w-full text-left px-3 py-2 rounded-lg ring-1 ${selectedId === d.id ? 'bg-secondary ring-secondary' : 'ring-secondary hover:bg-secondary/60'}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm text-primary">{d.name}</span>
                                                {hasWarn ? (
                                                    <Badge color="warning" size="sm" className="inline-flex items-center gap-1">
                                                        <AlertCircle className="size-3" />
                                                        {t('triage.visual.check')}
                                                    </Badge>
                                                ) : (
                                                    <Badge color="success" size="sm" className="inline-flex items-center gap-1">
                                                        <CheckCircle className="size-3" />
                                                        {t('triage.visual.ok')}
                                                    </Badge>
                                                )}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Col 2 - 25% */}
                        <div className="col-span-12 md:col-span-3 rounded-xl bg-primary p-4 ring-1 ring-secondary">
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <label htmlFor="doc-select" className="text-sm text-tertiary">{t('triage.visual.document')}</label>
                                    <select id="doc-select" value={selectedId} onChange={handleSelectChange} className="text-sm rounded-md ring-1 ring-secondary bg-primary px-2 py-1 dark:text-white">
                                        {documents.map((d) => (
                                            <option key={d.id} value={d.id}>{d.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="space-y-2">
                                    {COMMON_ATTRIBUTES.map((k) => (
                                        <div key={k} className="flex flex-col gap-1">
                                            <label className="text-xs text-tertiary">{k}</label>
                                            <input
                                                className="rounded-md bg-primary ring-1 ring-secondary px-2 py-1 text-sm dark:text-white"
                                                value={String(formValues[k] ?? '')}
                                                onChange={(e) => handleFormChange(k, e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Col 3 - 50% */}
                        <div className="col-span-12 md:col-span-6 rounded-xl bg-primary p-2 ring-1 ring-secondary h-[70vh]">
                            <PdfViewer fileUrl={selectedDoc.fileUrl} polygons={[]} />
                        </div>
                    </div>
                </Tabs.Panel>

                {/* Tab: Raw Data */}
                <Tabs.Panel id="raw" className="space-y-4">
                    <div className="rounded-xl bg-primary ring-1 ring-secondary p-4">
                        <h3 className="text-md font-semibold text-primary mb-3">{selectedDoc.name}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-xs text-tertiary">{t('triage.raw.json')}</label>
                                <textarea
                                    className="w-full h-64 rounded-lg bg-primary ring-1 ring-secondary p-2 font-mono text-xs dark:text-white"
                                    value={JSON.stringify(formValues, null, 2)}
                                    onChange={(e) => {
                                        try {
                                            const parsed = JSON.parse(e.target.value);
                                            setFormValues(parsed);
                                        } catch {
                                            // noop: lascia l'utente editare liberamente
                                        }
                                    }}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-tertiary">{t('triage.raw.highlightDiscrepancies')}</label>
                                <div className="mt-2 rounded-lg bg-secondary p-3 overflow-auto h-64">
                                    {COMMON_ATTRIBUTES.map((k) => {
                                        const warn = discrepancies[selectedId]?.has(k);
                                        return (
                                            <div key={k} className={`text-xs py-1 px-2 rounded ${warn ? 'bg-warning-50 text-warning-900' : 'text-tertiary'}`}>
                                                <span className="font-mono mr-2">{k}:</span>
                                                <span className="text-primary">{String(formValues[k] ?? '')}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Modal Risoluzione AI */}
            <ModalOverlay isOpen={openAI} onOpenChange={onCloseModal}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary w-full max-w-xl">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-primary">{t('triage.modal.title')}</h3>
                            </div>
                            {progress < 100 && (
                                <div className="space-y-4">
                                    <p className="text-sm text-tertiary">{t('triage.modal.analyzing')}</p>
                                    <ProgressBar value={progress} min={0} max={100} />
                                    <div className="text-right text-xs text-tertiary">{progress}%</div>
                                </div>
                            )}
                            {progress === 100 && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 text-success-700">
                                        <CheckCircle className="size-5" />
                                        <span className="text-md font-semibold">{t('triage.modal.completed')}</span>
                                    </div>
                                    <p className="text-sm text-tertiary">{t('triage.modal.allResolved')}</p>
                                </div>
                            )}
                            <div className="mt-6 flex justify-end gap-2">
                                <Button color="secondary" onClick={() => onCloseModal(false)}>{t('triage.modal.close')}</Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </div>
    );
};

export default ShipperContainerTriagePage;


